import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_AppConfig, ModuleFE_Dialog, TS_AppTools, TS_TextArea } from '@nu-art/thunderstorm-frontend/index';
import { QJSONViewer } from '@app/hcs-frontend';
import './ATS_FHIRQueryResources.scss';
import { __stringify, _keys, asArray, compare, tsValidate, tsValidateArray } from '@nu-art/ts-common';
import { ModuleFE_AdvisorSnippet } from '../../_entity.js';
import { ConfigKey_FHIRQueryResources_Key, Validator_FHIRQueryResource } from '@app/fhir-shared';
import { ConfigKeyFE_FHIRQueryResources } from '@app/fhir-frontend';
import { TS_Icons } from '@nu-art/ts-styles';
export class ATS_FHIRQueryResources extends ComponentSync {
    __onAppConfigsUpdated = (...params) => {
        const configs = asArray(params[1]);
        if (configs.some(config => config.key === ConfigKey_FHIRQueryResources_Key))
            this.reDeriveState();
    };
    static Screen = {
        key: 'fhir-query-resources',
        name: 'FHIR Query Resources',
        modulesToAwait: [ModuleFE_AppConfig, ModuleFE_AdvisorSnippet],
        renderer: this,
    };
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.resources = ConfigKeyFE_FHIRQueryResources.get() ?? [];
        state.raw = __stringify(state.resources, true);
        return state;
    }
    //######################### Logic #########################
    convertToJson = (cb) => {
        const raw = this.state.raw;
        let resources;
        //Parse
        try {
            resources = JSON.parse(raw);
        }
        catch (e) {
            const error = e;
            this.logError(error);
            return this.setState({ errors: error.message });
        }
        //Validate
        try {
            this.validateResources(resources);
            this.logInfo(resources);
        }
        catch (e) {
            const error = e;
            const results = this.cleanInvalidResults(error.result);
            return this.setState({ errors: results });
        }
        //Set State
        this.setState({ resources: resources, errors: undefined }, () => cb?.());
    };
    validateResources = (resources) => {
        return tsValidate(resources, tsValidateArray(Validator_FHIRQueryResource));
    };
    cleanInvalidResults = (_results) => {
        this.logError(_results);
        const results = { ..._results };
        this.logInfo(results);
        _keys(results).forEach(resourceIndex => {
            //If index holds no data
            if (!results[resourceIndex])
                return delete results[resourceIndex];
            //Clean params
            // @ts-ignore
            const params = results[resourceIndex]['params'];
            _keys(params ?? []).forEach(paramIndex => {
            });
        });
        return results;
    };
    revertChanges = () => {
        const raw = __stringify(this.state.resources, true);
        this.setState({ raw, errors: undefined });
    };
    uploadJSON = async () => {
        this.convertToJson(() => {
            const diffs = this.calculateDiffs();
            this.logWarning(diffs);
            if (!diffs.resources.length)
                return;
            Dialog_ActionConfirm.show({
                changedResources: diffs.resources,
                affectedSnippets: diffs.snippets,
                nextJSON: this.state.resources,
            });
        });
    };
    calculateDiffs = () => {
        const original = ConfigKeyFE_FHIRQueryResources.get();
        const current = this.state.resources;
        const changedResources = original.filter(resource => {
            const currentResource = current.find(_r => _r.resource === resource.resource);
            if (!currentResource)
                return true;
            return !compare(resource, currentResource);
        });
        const changedResourcesKeys = changedResources.map(r => r.resource);
        const snippets = ModuleFE_AdvisorSnippet.cache.filter(snippet => {
            if (!snippet.queryData?.resourceType)
                return false;
            return changedResourcesKeys.includes(snippet.queryData.resourceType);
        });
        return { resources: changedResources, snippets: snippets };
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { id: `ats__${ATS_FHIRQueryResources.Screen.key}`, children: [TS_AppTools.renderPageHeader(ATS_FHIRQueryResources.Screen.name), _jsxs(LL_H_C, { className: 'ats__body', children: [this.renderTextArea(), this.renderControls(), this.renderPrettyJSON()] })] });
    }
    renderPrettyJSON = () => {
        const toView = this.state.errors ?? this.state.resources;
        if (typeof toView === 'string')
            return _jsx("div", { className: 'message-view', children: toView });
        return _jsx(QJSONViewer, { item: toView });
    };
    renderTextArea = () => {
        return _jsx(TS_TextArea, { type: 'text', value: this.state.raw, onChange: val => this.setState({ raw: val }) });
    };
    renderControls = () => {
        return _jsxs(LL_V_L, { className: 'controls', children: [_jsx(Button, { variant: 'secondary', onClick: () => this.convertToJson(), children: "Convert Raw To JSON" }), _jsx(Button, { variant: 'secondary', onClick: this.revertChanges, children: "Revert" }), _jsx(Button, { variant: 'primary', onClick: this.uploadJSON, children: "Upload Changes" })] });
    };
}
class Dialog_ActionConfirm extends ComponentSync {
    //######################### Life Cycle #########################
    static show = (props) => {
        ModuleFE_Dialog.show({ content: _jsx(Dialog_ActionConfirm, { ...props }) });
    };
    //######################### Logic #########################
    closeDialog = () => {
        ModuleFE_Dialog.close(true);
    };
    executeAction = async () => {
        await ModuleFE_AdvisorSnippet._v1.updateFHIRQueryResources({ nextResources: this.props.nextJSON }).executeSync();
        this.closeDialog();
    };
    generateDescription = () => {
        let description = '';
        //Add changed resources to description
        description += `Found changes in ${this.props.changedResources.length} Resources:\n`;
        description += this.props.changedResources.map(r => r.resource).join(', ');
        //Add affected snippets to description
        description += `\n\nAs a result of this change, ${this.props.affectedSnippets.length} snippets will clean their fhir data:\n`;
        description += this.props.affectedSnippets.map(snippet => snippet.label).join(', ');
        return description;
    };
    //######################### Render #########################
    renderHeader = () => {
        return _jsx(LL_H_C, { className: 'dialog__header', children: _jsx("div", { className: 'dialog__header__title', children: "FHIR Query Resource Change Confirmation" }) });
    };
    renderMain = () => {
        const description = this.generateDescription();
        return _jsxs(LL_H_C, { className: 'dialog__main', children: [_jsx(TS_Icons.information.component, { className: 'dialog__main__warning-icon' }), _jsx(LL_V_L, { className: 'dialog__main__warning', children: _jsx("div", { className: 'dialog__main__warning__text', children: description }) })] });
    };
    renderButtons = () => {
        return _jsxs(LL_H_C, { className: 'dialog__buttons', children: [_jsx(Button, { variant: 'secondary', onClick: this.closeDialog, children: "Cancel" }), _jsx(Button, { variant: 'primary', onClick: this.executeAction, children: "Execute" })] });
    };
    render() {
        return _jsxs(LL_V_L, { id: 'dialog__fhir-resources-confirm', children: [this.renderHeader(), this.renderMain(), this.renderButtons()] });
    }
}
//# sourceMappingURL=ATS_FHIRQueryResources.js.map